import { useEffect, useRef, useState } from 'react';
import {Link, useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import AddIconWhite from '../../assets/icons/iconAddWhite.svg';
import './style.scss';
import UserAttendanceCard from '../../components/userAttendanceCard';
import {
  getBeneficiaries,
  getBeneficiariesBySchool,
  getMeetingsAttendance,
  saveMeetingAttendance,
  prepareAttendanceTemplate,
  createPublicAttendanceQrToken
} from '../../API/apiService';
import { toast } from 'react-toastify';
import { useLoader } from '../../contexts/loader/loader';
import { buildProtectedImageUrl, fetchWithAuth } from '../../utils/secureMedia';
import { buildEvidenceDownloadName } from '../../utils/evidenceFileName';
import {
  buildCombinedEvidencePdfBlob,
  buildCombinedEvidencePdfName
} from '../../utils/combinedEvidencePdf';
import EvidencePreviewModal from '../../components/evidencePreviewModal';
import { buildOfficialAttendancePdfBlob, buildPrefilledAttendancePdfBlob } from '../officialAttendanceReport';
import QrCode from '../../components/qrCode';
import { useUserName } from '../../contexts/userName';
import { isSocialControlMediatorRole } from './meetingFormMode';
import { resolveEvidenceSlot } from '../../utils/evidenceSlot';
import { getMeetingTopicCode, getMeetingTopicMaxLength } from '../../utils/meetingTopic';

const EVIDENCE_SECTIONS = [
  'Evidencia Fotografica',
  'Acta/guia',
  'Listado de asistencia'
];
const ADVISOR_PHASE_EVIDENCE_SECTIONS = {
  solicitud: ['Solicitud', 'Acta', 'Otro'],
  planeacion: ['Acta', 'Guia', 'Otro'],
  desarrollo: ['Acta', 'Guia', 'Otro'],
  cierre: ['Formulario de valoracion', 'Acta', 'Otro']
};
const ADVISOR_PHASE_EVIDENCE_PREFIXES = {
  solicitud: ['SolC', 'AcC', 'AsC'],
  planeacion: ['AcC', 'GuC', 'AsC'],
  desarrollo: ['AcC', 'GuC', 'AsC'],
  cierre: ['ValC', 'AcC', 'AsC']
};
const ADVISOR_PHASE_EVIDENCE_TYPES = {
  solicitud: 'COP_SOLICITUD',
  planeacion: 'COP_PLANEACION',
  desarrollo: 'COP_DESARROLLO',
  cierre: 'COP_CIERRE'
};
const MAX_REQUEST_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const TOTAL_EVIDENCE_IMAGES = 3;
const ADVISOR_PHASE_LABELS = {
  solicitud: 'Solicitud',
  planeacion: 'Planeacion',
  desarrollo: 'Desarrollo',
  cierre: 'Cierre'
};

const EVIDENCE_REVIEW_STATUS = {
  pending: {
    label: 'Pendiente de revisión',
    icon: 'schedule'
  },
  approved: {
    label: 'Aprobado',
    icon: 'check_circle'
  },
  needs_correction: {
    label: 'Requiere subsanación',
    icon: 'error'
  }
};

function getAdvisorPhaseLabel(phase) {
  const normalizedPhase = String(phase || '').trim().toLowerCase();
  return ADVISOR_PHASE_LABELS[normalizedPhase] || '';
}

function normalizeTextValue(value) {
  return value === null || value === undefined ? '' : value;
}

function isValid2026MeetingDate(value) {
  const normalized = String(value || '').substring(0, 10);
  if (!/^2026-\d{2}-\d{2}$/.test(normalized)) return false;

  const parsedDate = new Date(`${normalized}T00:00:00`);
  return !Number.isNaN(parsedDate.getTime()) &&
    parsedDate.getFullYear() === 2026 &&
    String(parsedDate.getMonth() + 1).padStart(2, '0') === normalized.substring(5, 7) &&
    String(parsedDate.getDate()).padStart(2, '0') === normalized.substring(8, 10);
}

function resolveMeetingEvidenceSlot(imageName, fallbackSlot = 1) {
  return resolveEvidenceSlot(imageName, fallbackSlot, TOTAL_EVIDENCE_IMAGES);
}

function SingleMeeting() {
  const { show } = useLoader();
  const { userRole } = useUserName();
  const navigate = useNavigate();
  const location = useLocation();
  // STATES //
  const [searchParams] = useSearchParams();
  const advisorPhase = String(
    searchParams.get('phase') ||
      location?.state?.advisorPhase ||
      location?.state?.phase ||
      ''
  ).trim().toLowerCase();
  const advisorPhaseLabel = getAdvisorPhaseLabel(advisorPhase);
  const isAdvisorPhaseMeeting = Boolean(advisorPhaseLabel);
  const hasAdvisorAttendance = advisorPhase === 'planeacion' || advisorPhase === 'desarrollo';
  const hasAttendance = !isAdvisorPhaseMeeting || hasAdvisorAttendance;
  const hasOptionalAdvisorPhaseTimes = isAdvisorPhaseMeeting;
  const evidenceSections = isAdvisorPhaseMeeting
    ? ADVISOR_PHASE_EVIDENCE_SECTIONS[advisorPhase] || EVIDENCE_SECTIONS
    : EVIDENCE_SECTIONS;
  const advisorMeetingOrder =
    searchParams.get('order') || location?.state?.advisorMeetingOrder || '';
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isDownloadingAttendanceTemplate, setIsDownloadingAttendanceTemplate] = useState(false);
  const stateBackTo = location?.state?.backTo;
  const adminReturnTo = searchParams.get('returnTo');
  const adminOrigin = searchParams.get('origin');
  const hasAdminReadOnlyOrigin =
    adminOrigin === 'admin-evidences' || adminOrigin === 'admin-attendances';
  const adminBackLabel =
    adminOrigin === 'admin-attendances' ? 'asistencias' : 'evidencias';
  const defaultAdminBackTo =
    adminOrigin === 'admin-attendances'
      ? '/administrador/menu/asistencias'
      : '/administrador/menu/evidencias';
  const adminReadOnlyBackTo =
    hasAdminReadOnlyOrigin
      ? (typeof stateBackTo === 'string' && stateBackTo) ||
        adminReturnTo ||
        defaultAdminBackTo
      : null;
  const isReadOnlyView = Boolean(adminReadOnlyBackTo);
  const isSocialControlMediator = isSocialControlMediatorRole(
    userRole || localStorage.getItem('ROLE')
  );
  const [previewEvidence, setPreviewEvidence] = useState(null);
  const [attendanceQr, setAttendanceQr] = useState(null);

  useEffect(() => {
    if (!attendanceQr?.url) return undefined;

    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [attendanceQr?.url]);
  const [isGeneratingAttendanceQr, setIsGeneratingAttendanceQr] = useState(false);
  const [info, setInfo] = useState({
    goal: '',
    topic: '',
    topicCode: '',
    place: '',
    address: '',
    hasCustomLocation: false,
    groupId: '',
    activityGroupId: '',
    meetingId: '',
    meetingNotes: '',
    meetingDate: '',
    startTime: '',
    endTime: '',
    meetingType: '',
    meetingTypeName: '',
    images: [],
    beneficiaries: [],
    order: '',
    edit: false,
    reviewStatus: 'pending',
    reviewObservations: ''
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const topicMaxLength = getMeetingTopicMaxLength(info.topicCode);

  const inputRefs = useRef([]);
  const setInputRef = (index, ref) => {
    inputRefs.current[index] = ref;
  };

  const [attendanceImages, setAttendanceImages] = useState([
    {
      imageUrl: '',
      id: 0,
      updated: false,
      file: null,
      base64: null,
      externalId: null,
      savedInDB: false,
      fileType: null,
      fileName: ''
    },
    {
      imageUrl: '',
      id: 1,
      updated: false,
      file: null,
      base64: null,
      externalId: null,
      savedInDB: false,
      fileType: null,
      fileName: ''
    },
    {
      imageUrl: '',
      id: 2,
      updated: false,
      file: null,
      base64: null,
      externalId: null,
      savedInDB: false,
      fileType: null,
      fileName: ''
    },
    {
      imageUrl: '',
      id: 3,
      updated: false,
      file: null,
      base64: null,
      externalId: null,
      savedInDB: false,
      fileType: null,
      fileName: ''
    },
    {
      imageUrl: '',
      id: 4,
      updated: false,
      file: null,
      base64: null,
      externalId: null,
      savedInDB: false,
      fileType: null,
      fileName: ''
    }
  ]);

  function navigateToBackTarget() {
    if (adminReadOnlyBackTo) {
      navigate(adminReadOnlyBackTo, { replace: true });
      return true;
    }

    if (typeof stateBackTo === 'string') {
      navigate(stateBackTo, { replace: true });
      return true;
    }

    if (stateBackTo?.pathname) {
      navigate(stateBackTo.pathname, {
        state: stateBackTo.state,
        replace: true
      });
      return true;
    }

    return false;
  }

  async function generateAttendanceQr(registrationMode = 'attendance_only') {
    if (
      !info.meetingId ||
      (!info.groupId && !info.activityGroupId && !info.schoolId) ||
      isGeneratingAttendanceQr
    ) return;
    setIsGeneratingAttendanceQr(true);
    try {
      const response = await createPublicAttendanceQrToken({
        ...(info.activityGroupId
          ? {activityGroupId: info.activityGroupId}
          : info.groupId
            ? {groupId: info.groupId, schoolId: info.schoolId}
            : {schoolId: info.schoolId}),
        meetingIds: [info.meetingId],
        registrationMode
      });
      if (!response?.token) return;
      const path = ['attendance_only', 'attendance_satisfaction'].includes(registrationMode)
        ? '/firma-encuentro'
        : '/registro-jornada';
      const url = new URL(
        `${path}?token=${encodeURIComponent(response.token)}`,
        window.location.origin
      ).href;
      setAttendanceQr({url, expiresAt: response.expiresAt, registrationMode});
    } finally {
      setIsGeneratingAttendanceQr(false);
    }
  }

  async function copyAttendanceQrLink() {
    try {
      await navigator.clipboard.writeText(attendanceQr.url);
      toast('Enlace copiado', {type: 'success'});
    } catch (error) {
      toast('No se pudo copiar el enlace', {type: 'warning'});
    }
  }

  function getBreadcrumbTarget() {
    if (adminReadOnlyBackTo) return adminReadOnlyBackTo;
    if (typeof stateBackTo === 'string') return stateBackTo;
    return stateBackTo?.pathname || '/escuelas/grupo/lista_de_encuentros';
  }

  function getBreadcrumbState() {
    if (adminReadOnlyBackTo || typeof stateBackTo === 'string') return undefined;
    return stateBackTo?.state || {
      group: location?.state?.group ?? info.groupId,
      school: location?.state?.school
    };
  }

  function closePreview() {
    setPreviewEvidence(null);
  }

  function openEvidencePreview(index) {
    const imageInfo = attendanceImages[index];
    if (!imageInfo?.updated) return;

    const sourceUrl = imageInfo?.externalId
      ? buildProtectedImageUrl(imageInfo.externalId, false, Date.now())
      : imageInfo?.imageUrl;

    if (!sourceUrl) return;

    setPreviewEvidence({
      sourceUrl,
      fileType: imageInfo?.fileType === 'pdf' ? 'pdf' : 'image',
      fileName: getEvidenceDisplayName(imageInfo, index)
    });
  }

  function handleImageClick(index) {
    if (isReadOnlyView) {
      openEvidencePreview(index);
      return;
    }

    const fileInput = inputRefs.current[index];
    if (!fileInput) return;
    fileInput.value = '';
    fileInput.click();
  }

  function normalizeFileType(file, pdfOnly = false) {
    const mime = String(file?.type || '').toLowerCase();
    const name = String(file?.name || '').toLowerCase();

    if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (pdfOnly) return null;

    if (
      mime === 'image/jpeg' ||
      mime === 'image/jpg' ||
      name.endsWith('.jpeg') ||
      name.endsWith('.jpg')
    ) {
      return 'image';
    }

    return null;
  }

  function handleCaptureImage(target, identifier) {
    if (!target.files || target.files.length === 0) return;

    const file = target.files[0];
    const detectedType = normalizeFileType(file);
    if (!detectedType) {
      toast('Solo se permiten archivos PDF, JPG o JPEG', {
        type: 'warning'
      });
      target.value = '';
      return;
    }
    const selectedFilesSize = attendanceImages.reduce((total, image) => {
      if (image.id === identifier || !image.file || image.generatedVirtual) return total;
      return total + Number(image.file.size || 0);
    }, 0);
    if (selectedFilesSize + Number(file.size || 0) > MAX_REQUEST_FILE_SIZE_BYTES) {
      toast('Se excedió el tamaño máximo permitido: los archivos seleccionados no pueden superar 5 MB en total', {
        type: 'warning'
      });
      target.value = '';
      return;
    }

    const newUrl = URL.createObjectURL(file);
    const updatedImageList = [...attendanceImages];
    const img = updatedImageList.find(a => a.id === identifier);

    if (img) {
      img.imageUrl = newUrl;
      img.updated = true;
      img.file = file;
      img.fileType = detectedType;
      img.fileName = file.name || '';
      img.base64 = null;
      img.savedInDB = false;
      img.externalId = null;
    }
    setAttendanceImages([...updatedImageList]);
  }

  function addInfo(id, personalId, selectedValue) {
    if (personalId === 'goal' && id?.length > 2000) {
      id = id.substring(0, 2000);
    }
    if (personalId === 'meetingNotes' && id?.length > 255) {
      id = id.substring(0, 255);
    }
    if (selectedValue) {
      const valueName = `${personalId}Value`;
      setInfo({ ...info, [personalId]: id, [valueName]: selectedValue });
    } else {
      setInfo({ ...info, [personalId]: id });
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  function base64ToBlob(base64String) {
    const parts = String(base64String || '').split(',');
    if (parts.length < 2) return null;

    const mimeMatch = parts[0].match(/^data:([^;]+);base64$/i);
    const mime = mimeMatch?.[1] || 'application/octet-stream';
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  function getEvidenceDisplayName(imgInfo, index) {
    const raw = String(imgInfo?.fileName || '').trim();
    if (!raw) return imgInfo?.updated ? `evidencia_${index + 1}` : '';
    return raw.split(/[\\/]/).pop() || raw;
  }

  function getOriginalEvidenceUrl(imageInfo) {
    if (imageInfo?.externalId) {
      return buildProtectedImageUrl(imageInfo.externalId, false, Date.now());
    }
    return imageInfo?.imageUrl || '';
  }

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  function downloadByUrl(sourceUrl, fileName = '') {
    if (!sourceUrl) return;

    const link = document.createElement('a');
    link.href = sourceUrl;
    if (fileName) {
      link.download = fileName;
    }
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  }

  async function resolveImageEvidenceAsset(imageInfo) {
    if (imageInfo?.file) {
      return {
        blob: imageInfo.file,
        dataUrl: await imageToDataUrl(imageInfo),
        sourceUrl: '',
        usedRemoteFallback: false
      };
    }

    if (imageInfo?.base64 && String(imageInfo.base64).startsWith('data:image/')) {
      return {
        blob: base64ToBlob(imageInfo.base64),
        dataUrl: await imageToDataUrl(imageInfo),
        sourceUrl: '',
        usedRemoteFallback: false
      };
    }

    const sourceUrl = getOriginalEvidenceUrl(imageInfo);
    if (!sourceUrl) {
      return {
        blob: null,
        dataUrl: null,
        sourceUrl: '',
        usedRemoteFallback: false
      };
    }

    try {
      const response = await fetchWithAuth(sourceUrl);
      if (!response.ok) {
        return {
          blob: null,
          dataUrl: sourceUrl,
          sourceUrl,
          usedRemoteFallback: true
        };
      }

      const blob = await response.blob();
      return {
        blob,
        dataUrl: await blobToDataUrl(blob),
        sourceUrl,
        usedRemoteFallback: false
      };
    } catch (error) {
      return {
        blob: null,
        dataUrl: sourceUrl,
        sourceUrl,
        usedRemoteFallback: true
      };
    }
  }

  async function imageToDataUrl(imageInfo) {
    if (imageInfo?.file) {
      return blobToDataUrl(imageInfo.file);
    }

    if (imageInfo?.base64 && String(imageInfo.base64).startsWith('data:image/')) {
      return imageInfo.base64;
    }

    const sourceUrl = getOriginalEvidenceUrl(imageInfo);
    if (!sourceUrl) return null;
    try {
      const response = await fetchWithAuth(sourceUrl);
      if (!response.ok) return sourceUrl;
      const blob = await response.blob();
      return blobToDataUrl(blob);
    } catch (error) {
      // Fallback sin fetch para evitar romper la generaciÃ³n por CORS
      return sourceUrl;
    }
  }

  async function downloadPdfEvidence(imageInfo, index, overrideName) {
    let blob = null;
    const sourceUrl = getOriginalEvidenceUrl(imageInfo);

    if (imageInfo?.base64 && String(imageInfo.base64).startsWith('data:application/pdf')) {
      blob = base64ToBlob(imageInfo.base64);
    } else if (sourceUrl) {
      try {
        const response = await fetchWithAuth(sourceUrl);
        if (response.ok) {
          blob = await response.blob();
        }
      } catch (error) {
        // Fallback: abrir enlace directo si CORS bloquea fetch
      }
    }

    if (blob) {
      const rawName = String(overrideName || imageInfo?.fileName || '').trim();
      const fileName = rawName
        ? (/\.pdf$/i.test(rawName) ? rawName : `${rawName}.pdf`)
        : `evidencia_${index + 1}.pdf`;
      downloadBlob(blob, fileName);
      return;
    }

    if (sourceUrl) {
      const link = document.createElement('a');
      link.href = sourceUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
  }

  function getEvidencePrefix(index) {
    if (isAdvisorPhaseMeeting) {
      const advisorPrefixes = ADVISOR_PHASE_EVIDENCE_PREFIXES[advisorPhase];
      return advisorPrefixes?.[index] ?? `C${index + 1}`;
    }

    const prefixes = ['C', 'GuC', 'AsC'];
    return prefixes[index] ?? `C${index + 1}`;
  }

  function getEvidenceDownloadName(imgInfo, index) {
    const ext = imgInfo?.fileType === 'pdf' ? 'pdf' : 'jpg';
    const municipalityId =
      info?.municipalityId ??
      info?.municipality_id ??
      info?.meeting?.municipalityId ??
      info?.meeting?.municipality_id ??
      info?.groupMunicipalityId ??
      null;
    const groupCode =
      info?.groupCode ??
      info?.group?.code ??
      '';
    const orderValue =
      info?.order ??
      info?.meetingOrder ??
      info?.meeting?.order ??
      info?.meeting?.orderNumber ??
      '';
    const hasVirtualAttendance = attendanceImages.some(
      evidence => evidence?.generatedVirtual === true
    );
    const prefix = !isAdvisorPhaseMeeting && imgInfo?.generatedVirtual
      ? 'AsC'
      : !isAdvisorPhaseMeeting && index === 2 && hasVirtualAttendance
        ? 'OtC'
        : getEvidencePrefix(index);
    const schoolCode =
      info?.schoolCode ??
      info?.school?.code ??
      location?.state?.school?.code ??
      '';

    return buildEvidenceDownloadName({
      prefix,
      evidenceType: isAdvisorPhaseMeeting
        ? ADVISOR_PHASE_EVIDENCE_TYPES[advisorPhase]
        : '',
      municipalityId,
      meetingDate: info?.meetingDate,
      orderValue,
      schoolCode,
      groupCode,
      extension: ext,
      fallbackFileName: imgInfo?.fileName,
      fallbackIndex: index + 1
    });
  }

  function getDownloadableMeetingEvidences() {
    return attendanceImages
      .map((img, slotIndex) => ({ ...img, slotIndex }))
      .filter(img => img.updated && (img.imageUrl || img.file || img.base64));
  }

  function getCombinedMeetingEvidenceName(evidences) {
    const firstEvidence = evidences?.[0];
    const firstName = firstEvidence
      ? getEvidenceDownloadName(
          { ...firstEvidence, fileType: 'pdf' },
          firstEvidence.slotIndex
        )
      : '';
    return buildCombinedEvidencePdfName(firstName, 'evidencias_asistencia');
  }

  async function handleDownloadUnifiedMeetingEvidences() {
    const evidences = getDownloadableMeetingEvidences();

    if (evidences.length === 0) {
      toast('No hay evidencias para descargar', {
        type: 'warning'
      });
      return;
    }

    setButtonDisabled(true);
    await show(true);

    try {
      const pdfBlob = await buildCombinedEvidencePdfBlob(
        evidences.map(evidence => ({
          file: evidence.file,
          base64: evidence.base64,
          fileType: evidence.fileType,
          fileName:
            evidence.fileName ||
            getEvidenceDownloadName(evidence, evidence.slotIndex),
          sourceUrl:
            evidence.file || evidence.base64 ? '' : getOriginalEvidenceUrl(evidence)
        }))
      );

      downloadBlob(pdfBlob, getCombinedMeetingEvidenceName(evidences));
      toast('Descarga unida iniciada', {
        type: 'success'
      });
    } catch (error) {
      console.error('DOWNLOAD_UNIFIED_MEETING_EVIDENCES_ERROR', error);
      toast('No se pudo generar el PDF unido', {
        type: 'error'
      });
    }

    await show(false);
    setButtonDisabled(false);
  }

  async function handleDownloadMeetingReport() {
    const evidences = getDownloadableMeetingEvidences();

    if (evidences.length === 0) {
      toast('No hay evidencias para descargar', {
        type: 'warning'
      });
      return;
    }

    setButtonDisabled(true);
    await show(true);

    try {
      const uploadedAttendance = evidences.find(
        evidence => evidence.slotIndex === 2 && evidence.generatedVirtual !== true
      );
      const virtualAttendance = evidences.find(
        evidence => evidence.generatedVirtual === true
      );
      const attendanceEntriesToCombine = uploadedAttendance && virtualAttendance
        ? [uploadedAttendance, virtualAttendance]
        : [];
      if (attendanceEntriesToCombine.length > 0) {
        const combinedAttendancePdf = await buildCombinedEvidencePdfBlob(
          attendanceEntriesToCombine.map(evidence => ({
            file: evidence.file,
            base64: evidence.base64,
            fileType: evidence.fileType,
            fileName: evidence.fileName,
            sourceUrl:
              evidence.file || evidence.base64 ? '' : getOriginalEvidenceUrl(evidence)
          }))
        );
        const combinedAttendanceName = getEvidenceDownloadName(
          {
            ...uploadedAttendance,
            fileType: 'pdf',
            fileName: 'AsC3.pdf',
            generatedVirtual: true
          },
          uploadedAttendance.slotIndex
        );
        downloadBlob(combinedAttendancePdf, combinedAttendanceName);
      }

      const separateEvidences = attendanceEntriesToCombine.length > 0
        ? evidences.filter(evidence => !attendanceEntriesToCombine.includes(evidence))
        : evidences;
      const evidenceImages = [];
      const pdfEvidences = [];
      let usedRemoteImageFallback = false;

      for (const evidence of separateEvidences) {
        const evidenceFileName = getEvidenceDownloadName(
          evidence,
          evidence.slotIndex
        );

        if (evidence.file && evidence.fileType === 'pdf') {
          downloadBlob(evidence.file, evidenceFileName);
          continue;
        }

        if (evidence.fileType === 'pdf') {
          pdfEvidences.push(evidence);
          continue;
        }

        const imageAsset = await resolveImageEvidenceAsset(evidence);
        if (imageAsset.blob) {
          downloadBlob(imageAsset.blob, evidenceFileName);
        } else if (imageAsset.sourceUrl) {
          downloadByUrl(imageAsset.sourceUrl, evidenceFileName);
        }

        if (imageAsset.dataUrl) {
          evidenceImages.push(imageAsset.dataUrl);
        }

        if (imageAsset.usedRemoteFallback) {
          usedRemoteImageFallback = true;
        }
      }

      const remoteImageCount =
        usedRemoteImageFallback ||
        evidenceImages.some(src => String(src).startsWith('http'));

      for (const evidence of pdfEvidences) {
        const pdfName = getEvidenceDownloadName(evidence, evidence.slotIndex);
        await downloadPdfEvidence(evidence, evidence.slotIndex, pdfName);
      }

      const attendedBeneficiaries = isAdvisorPhaseMeeting
        ? []
        : beneficiaries.filter(b => b.attended === true);
      const beneficiariesHtml = attendedBeneficiaries.length > 0
        ? attendedBeneficiaries
          .map((b, idx) => `<li>${idx + 1}. ${escapeHtml(b.full_name)}${b.document ? ` - ${escapeHtml(b.document)}` : ''}</li>`)
          .join('')
        : '<li>No hay beneficiarios registrados con asistencia.</li>';
      const beneficiariesSectionHtml = isAdvisorPhaseMeeting
        ? ''
        : `
            <hr />
            <h2 style="margin-bottom:10px;">Beneficiarios que asistieron</h2>
            <ol style="padding-left:20px;">
              ${beneficiariesHtml}
            </ol>
          `;

      const imagesHtml = evidenceImages.length > 0
        ? evidenceImages
          .map((src, idx) => `
            <div style="margin-bottom:12px;">
              <p style="margin:0 0 6px 0;font-size:13px;"><strong>Evidencia ${idx + 1}</strong></p>
              <img src="${src}" alt="Evidencia ${idx + 1}" style="max-width:580px;max-height:320px;border:1px solid #ccc;border-radius:6px;" />
            </div>
          `)
          .join('')
        : '<p>No hay imágenes de evidencias para incluir.</p>';

      const htmlDoc = `
        <html>
          <head>
            <meta charset="UTF-8" />
            <title>Acta de encuentro</title>
          </head>
          <body style="font-family: Arial, sans-serif; color:#222;">
            <h1 style="margin-bottom:10px;">${isAdvisorPhaseMeeting ? 'Reporte de Asesoria' : 'Reporte de Encuentro'}</h1>
            <p><strong>Fecha:</strong> ${escapeHtml(info.meetingDate || 'Sin fecha')}</p>
            <p><strong>Objetivo:</strong> ${escapeHtml(info.goal || 'Sin objetivo')}</p>
            <p><strong>Observaciones:</strong> ${escapeHtml(info.meetingNotes || 'Sin observaciones')}</p>
            <hr />
            <h2 style="margin-bottom:10px;">Evidencias (imágenes)</h2>
            ${imagesHtml}
            ${beneficiariesSectionHtml}
          </body>
        </html>
      `;

      const wordBlob = new Blob(['\ufeff', htmlDoc], { type: 'application/msword' });
      const reportOrder = info?.order || '';
      const reportGroupCode = info?.groupCode || '';
      const reportName =
        reportOrder && reportGroupCode
          ? `InfAs_enc${reportOrder}${reportGroupCode}.doc`
          : `InfAs_${info?.meetingId || 'reporte'}.doc`;
      downloadBlob(wordBlob, reportName);

      if (remoteImageCount) {
        toast('El Word se descargó, pero algunas imágenes pueden depender de conexión al abrir el archivo.', {
          type: 'info'
        });
      }

      toast('Descarga generada correctamente', {
        type: 'success'
      });
    } catch (error) {
      console.error('DOWNLOAD_MEETING_REPORT_ERROR', error);
      toast('No se pudo generar la descarga', {
        type: 'error'
      });
    }

    await show(false);
    setButtonDisabled(false);
  }

  /*  function clear() {
         setAttendanceImages([{imageUrl: '', id: 0, updated: false, base64: null, externalId: null},
             {imageUrl: '', id: 1, updated: false, base64: null, externalId: null},
             {imageUrl: '', id: 2, updated: false, base64: null, externalId: null}])
     } */

  async function downloadAttendanceTemplate() {
    if (buttonDisabled || isDownloadingAttendanceTemplate) return;
    if (['meetingDate', 'startTime', 'endTime', 'topic', 'goal'].some(key => !String(info[key] || '').trim())) {
      toast('Complete fecha, hora de inicio, hora final, tema y objetivo para descargar el listado.', { type: 'warning' });
      return;
    }
    if (!isValid2026MeetingDate(info.meetingDate)) {
      toast('Fecha no válida. Debe corresponder al año 2026', { type: 'warning' });
      return;
    }
    if (info.endTime <= info.startTime) {
      toast('La hora de finalizacion debe ser posterior al inicio', { type: 'warning' });
      return;
    }
    if (info.topic.length > topicMaxLength) {
      toast(`El tema permite máximo ${topicMaxLength} caracteres para incluir el código`, { type: 'warning' });
      return;
    }
    if (info.hasCustomLocation && (!info.place.trim() || !info.address.trim())) {
      toast('Ingrese el lugar y la dirección del encuentro.', { type: 'warning' });
      return;
    }
    setIsDownloadingAttendanceTemplate(true);
    let detailsSaved = false;
    await show(true);
    try {
      const result = await prepareAttendanceTemplate(info);
      if (!result?.meeting) throw new Error('No se pudo guardar la información del encuentro.');
      detailsSaved = true;
      const saved = result.meeting;
      setInfo(previous => ({
        ...previous,
        meetingDate: String(saved.meeting_date).substring(0, 10),
        startTime: String(saved.start_time).substring(0, 5),
        endTime: String(saved.end_time).substring(0, 5),
        topic: saved.topic,
        topicCode: getMeetingTopicCode(saved),
        goal: saved.goal,
        place: normalizeTextValue(saved.place),
        address: normalizeTextValue(saved.address),
        hasCustomLocation: Boolean(
          String(saved.place || '').trim() || String(saved.address || '').trim()
        ),
        edit: true
      }));
      const blob = await buildPrefilledAttendancePdfBlob(saved, result.beneficiaries);
      if (!blob) throw new Error('No hay un formato de asistencia disponible para este encuentro.');
      const code = getMeetingTopicCode(saved).replace(/[\\/:*?"<>|]+/g, '-');
      downloadBlob(blob, `Listado de asistencia para firma${code ? ` - ${code}` : ''}.pdf`);
      toast('Información guardada y listado descargado para firmar en físico.', { type: 'success' });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'No se pudo descargar el listado.';
      toast(detailsSaved ? `La información quedó guardada. ${message}` : message, { type: 'error' });
    } finally {
      await show(false);
      setIsDownloadingAttendanceTemplate(false);
    }
  }

  async function saveMeeting() {
    if (info.topic.length > topicMaxLength) {
      toast(`El tema permite máximo ${topicMaxLength} caracteres para incluir el código`, {
        type: 'warning'
      });
      return;
    }
    if (info.hasCustomLocation && (!info.place.trim() || !info.address.trim())) {
      toast('Ingrese el lugar y la dirección del encuentro.', { type: 'warning' });
      return;
    }
    const uploadSize = attendanceImages.reduce((total, image) => {
      if (!image.file || image.generatedVirtual) return total;
      return total + Number(image.file.size || 0);
    }, 0);
    if (uploadSize > MAX_REQUEST_FILE_SIZE_BYTES) {
      toast('Se excedió el tamaño máximo permitido: los archivos seleccionados no pueden superar 5 MB en total', {
        type: 'warning'
      });
      return;
    }
    if (!isSocialControlMediator && !info.goal) {
      toast('Debe definir un objetivo', {
        type: 'warning'
      });
      return;
    }
    if (
      hasAttendance &&
      beneficiaries.find(a => a.attended === null || a.attended === undefined)
    ) {
      toast('Debe tomar la asistencia de todos los beneficiarios', {
        type: 'warning'
      });
      return;
    }
    if (!info.meetingType) {
      toast('Debe Seleccionar un tipo de reunion', {
        type: 'warning'
      });
      return;
    }
    if (!info.meetingDate) {
      toast('Seleccione una fecha', {
        type: 'warning'
      });
      return;
    }
    if (!isValid2026MeetingDate(info.meetingDate)) {
      toast('Fecha no válida. Debe corresponder al año 2026', {
        type: 'warning'
      });
      return;
    }
    if (
      (!hasOptionalAdvisorPhaseTimes && (!info.startTime || !info.endTime)) ||
      (hasOptionalAdvisorPhaseTimes && Boolean(info.startTime) !== Boolean(info.endTime))
    ) {
      toast('Ingrese la hora de inicio y finalizacion', { type: 'warning' });
      return;
    }
    if (info.startTime && info.endTime && info.endTime <= info.startTime) {
      toast('La hora de finalizacion debe ser posterior al inicio', { type: 'warning' });
      return;
    }
    setButtonDisabled(true);
    await show(true);
    const payload = {
      ...info,
      beneficiaries: hasAttendance ? beneficiaries : [],
      images: attendanceImages.filter(i => i.file && !i.generatedVirtual),
      meetingType: 'PRE',
      meetingTypeName: 'Presencial',
      meetingTypeValue: 'Presencial'
    };

    const response = await saveMeetingAttendance(payload);
    await show(false);
    setButtonDisabled(false);
    if (response) {
      toast(isAdvisorPhaseMeeting ? 'Encuentro guardado satisfactoriamente' : 'Asistencia Guardada Satisfactoriamente', {
        type: 'success'
      });
      if (navigateToBackTarget()) {
        return;
      }

      navigate('/escuelas/grupo/lista_de_encuentros', {
        state: {
          group: location?.state?.group ?? info.groupId,
          school: location?.state?.school
        }
      });
    }
  }

  async function getInfo(meetingId) {
    const meetingInfo = await getMeetingsAttendance(meetingId);
    if (meetingInfo) {
      const advisorSchoolId =
        meetingInfo.meeting?.school?.id ??
        location?.state?.school?.id ??
        location?.state?.school ??
        '';
      const list = hasAdvisorAttendance
        ? await getBeneficiariesBySchool(advisorSchoolId)
        : isAdvisorPhaseMeeting
          ? []
          : await getBeneficiaries(null, meetingId);
      const imgsRaw = Array.isArray(meetingInfo.meetingImages)
        ? meetingInfo.meetingImages
        : [];

      const cacheKey = Date.now();
      const imgs = Array.from({ length: TOTAL_EVIDENCE_IMAGES }, (_, index) => ({
        imageUrl: '',
        id: index,
        updated: false,
        file: null,
        base64: null,
        externalId: null,
        savedInDB: false,
        fileType: null,
        fileName: ''
      }));

      imgsRaw.forEach((image, index) => {
        const fallbackSlot = Math.min(index + 1, TOTAL_EVIDENCE_IMAGES);
        const slot = resolveMeetingEvidenceSlot(image?.name, fallbackSlot);
        const slotIndex = slot - 1;

        imgs[slotIndex] = {
          imageUrl: buildProtectedImageUrl(image.id, !!image.thumbnail, cacheKey),
          id: slotIndex,
          updated: true,
          file: null,
          base64: null,
          externalId: image.id,
          savedInDB: true,
          fileType: image.type === 'pdf' ? 'pdf' : 'image',
          fileName: image.name ?? ''
        };
      });

      let nextMeetingDate = '';
      if (meetingInfo.meeting?.meeting_date) {
        const idx = meetingInfo.meeting.meeting_date.indexOf('T');
        if (idx > 0) {
          nextMeetingDate = meetingInfo.meeting.meeting_date.substring(0, idx);
        } else {
          nextMeetingDate = meetingInfo.meeting.meeting_date;
        }
      }
      const nextGoal = normalizeTextValue(meetingInfo.meeting?.goal);
      const nextMeetingNotes = normalizeTextValue(meetingInfo.meeting?.notes);
      const nextInfo = {
        meetingId,
        groupId: meetingInfo.meeting?.group_id ?? '',
        activityGroupId:
          meetingInfo.meeting?.activity_group_id ??
          meetingInfo.meeting?.activityGroup?.id ??
          location?.state?.activityGroup?.id ??
          '',
        goal: nextGoal,
        topic: normalizeTextValue(meetingInfo.meeting?.topic),
        topicCode: getMeetingTopicCode(meetingInfo.meeting),
        place: normalizeTextValue(meetingInfo.meeting?.place),
        address: normalizeTextValue(meetingInfo.meeting?.address),
        hasCustomLocation: Boolean(
          String(meetingInfo.meeting?.place || '').trim() ||
          String(meetingInfo.meeting?.address || '').trim()
        ),
        meetingNotes: nextMeetingNotes,
        meetingDate: normalizeTextValue(nextMeetingDate),
        startTime: normalizeTextValue(
          meetingInfo.meeting?.start_time ?? meetingInfo.meeting?.startTime
        ).substring(0, 5),
        endTime: normalizeTextValue(
          meetingInfo.meeting?.end_time ?? meetingInfo.meeting?.endTime
        ).substring(0, 5),
        meetingType: 'PRE',
        meetingTypeName: 'Presencial',
        meetingTypeValue: 'Presencial',
        order: meetingInfo.meeting?.order ?? meetingInfo.meeting?.order_number ?? '',
        reviewStatus:
          meetingInfo.meeting?.review_status ??
          meetingInfo.meeting?.reviewStatus ??
          'pending',
        reviewObservations:
          meetingInfo.meeting?.review_observations ??
          meetingInfo.meeting?.reviewObservations ??
          '',
        edit: !!(meetingInfo.meeting?.meeting_date && nextGoal),
        groupCode: meetingInfo.meeting?.group?.code ?? '',
        schoolId:
          meetingInfo.meeting?.school?.id ??
          meetingInfo.meeting?.group?.school?.id ??
          meetingInfo.meeting?.group?.schoolId ??
          meetingInfo.meeting?.group?.school_id ??
          location?.state?.school?.id ??
          location?.state?.school ??
          '',
        schoolCode:
          meetingInfo.meeting?.school?.code ??
          meetingInfo.meeting?.group?.school?.code ??
          location?.state?.school?.code ??
          '',
        programScope: String(
          meetingInfo.meeting?.school?.program_scope ??
          meetingInfo.meeting?.school?.programScope ??
          meetingInfo.meeting?.group?.school?.program_scope ??
          meetingInfo.meeting?.group?.school?.programScope ??
          location?.state?.school?.program_scope ??
          location?.state?.school?.programScope ??
          ''
        ).trim().toUpperCase(),
        municipalityId:
          meetingInfo.meeting?.group?.municipalityId ??
          meetingInfo.meeting?.group?.municipality_id ??
          meetingInfo.meeting?.group?.school?.municipalityId ??
          meetingInfo.meeting?.group?.school?.municipality_id ??
          meetingInfo.meeting?.group?.school?.municipality?.id ??
          meetingInfo.meeting?.school?.municipalityId ??
          meetingInfo.meeting?.school?.municipality_id ??
          meetingInfo.meeting?.school?.municipality?.id ??
          meetingInfo.meeting?.municipalityId ??
          meetingInfo.meeting?.municipality_id ??
          meetingInfo.meeting?.municipality?.id ??
          null
      };
      setInfo(prev => ({
        ...prev,
        ...nextInfo
      }));

      const virtualAttendances = Array.isArray(meetingInfo?.attendances)
        ? meetingInfo.attendances.filter(
          attendance =>
            (attendance.attended === true || attendance.attended === 1) &&
            Boolean(attendance?.image?.id)
        )
        : [];
      if (
        hasAttendance &&
        virtualAttendances.length > 0
      ) {
        try {
          const attendancePdf = await buildOfficialAttendancePdfBlob(
            meetingInfo.meeting,
            virtualAttendances,
            list || []
          );
          if (attendancePdf) {
            imgs.push({
              id: TOTAL_EVIDENCE_IMAGES,
              updated: true,
              file: attendancePdf,
              imageUrl: window.URL.createObjectURL(attendancePdf),
              base64: null,
              externalId: null,
              savedInDB: false,
              fileType: 'pdf',
              fileName: 'Listado de asistencia virtual.pdf',
              generatedVirtual: true
            });
          }
        } catch (error) {
          console.error('VIRTUAL_ATTENDANCE_PDF_ERROR', error);
        }
      }

      if (imgs.some(image => image.updated)) {
        setAttendanceImages(imgs);
      }
      if (
        hasAttendance &&
        meetingInfo?.attendances &&
        meetingInfo?.attendances.length > 0
      ) {
        const beneficiariesById = new Map(
          (Array.isArray(list) ? list : []).map(beneficiary => [
            Number(beneficiary.id),
            beneficiary
          ])
        );
        meetingInfo.attendances.forEach(attendance => {
          const attendanceBeneficiary = attendance.beneficiary;
          const beneficiaryId = Number(
            attendance.beneficiary_id ??
            attendance.beneficiaryId ??
            attendanceBeneficiary?.id
          );
          if (beneficiaryId && !beneficiariesById.has(beneficiaryId)) {
            beneficiariesById.set(beneficiaryId, {
              ...attendanceBeneficiary,
              id: beneficiaryId,
              full_name:
                attendanceBeneficiary?.full_name ??
                attendanceBeneficiary?.fullName ??
                '',
              document: attendanceBeneficiary?.document ?? ''
            });
          }
        });

        const beneficiariesList = Array.from(beneficiariesById.values()).map(b => {
          const benInfo = meetingInfo.attendances.find(
            a => Number(a.beneficiary_id ?? a.beneficiaryId) === Number(b.id)
          );
          const beneficiaryData = {
            id: b.id,
            full_name: b.full_name ?? b.fullName ?? '',
            document: b.document
          };
          if (benInfo) {
            beneficiaryData.savedInMeeting = true;
            beneficiaryData.attended = benInfo.attended;
            beneficiaryData.argument = benInfo.argument;
            beneficiaryData.notes = benInfo.notes;
            if (beneficiaryData.attended === true && benInfo.image) {
              beneficiaryData.imageId = benInfo.image.id;
              beneficiaryData.thumbnail = benInfo.image.thumbnail;
            }
          }
          return beneficiaryData;
        });
        setBeneficiaries(beneficiariesList);
      } else {
        if (hasAttendance && list) {
          setBeneficiaries(
            list.map(b => {
              return {
                id: b.id,
                full_name: b.full_name,
                document: b.document,
                savedInMeeting: false
              };
            })
          );
        } else if (!hasAttendance) {
          setBeneficiaries([]);
        }
      }
    }
  }

  useEffect(() => {
    const entries = Object.fromEntries(searchParams);
    getInfo(entries?.encuentro);
  }, []);

  return (
    <section className='singleMetting-main-container'>
      <EvidencePreviewModal
        open={Boolean(previewEvidence?.sourceUrl)}
        sourceUrl={previewEvidence?.sourceUrl}
        fileType={previewEvidence?.fileType}
        fileName={previewEvidence?.fileName}
        onClose={closePreview}
      />
      {attendanceQr?.url && (
        <div className='single-meeting-qr-modal' role='dialog' aria-modal='true'>
          <section>
            <button type='button' className='single-meeting-qr-modal__close'
                    aria-label='Cerrar' onClick={() => setAttendanceQr(null)}>
              <span className='material-symbols-rounded'>close</span>
            </button>
            <h2>
              {attendanceQr.registrationMode === 'attendance_characterization'
                ? 'QR de caracterización + asistencia'
                : attendanceQr.registrationMode === 'attendance_satisfaction'
                  ? 'QR de asistencia + formulario de satisfacción'
                  : 'QR para firmar asistencia'}
            </h2>
            <p>Encuentro {info.order}</p>
            <QrCode value={attendanceQr.url} size={300} className='single-meeting-qr-modal__code' />
            <small>{attendanceQr.url}</small>
            <p>Enlace vigente hasta {new Date(attendanceQr.expiresAt).toLocaleString('es-CO')}</p>
            <p>La asistencia cierra 2 horas después de la primera firma de esta sesión, o 24 horas para diplomados.</p>
            <button type='button' className='single-meeting-qr-modal__copy'
                    onClick={copyAttendanceQrLink}>Copiar enlace</button>
          </section>
        </div>
      )}
      <div  className='bread-crumbs'>
        / <Link
            to={getBreadcrumbTarget()}
            state={getBreadcrumbState()}
          >
            {adminReadOnlyBackTo
              ? adminBackLabel
              : isAdvisorPhaseMeeting
                ? 'asesorias'
                : 'lista_de_encuentros'}
          </Link>
      </div>
      <div className='principal-container'>
        <div className='title-container'>
          <div>
            <p className='description'>
              {isAdvisorPhaseMeeting
                ? `${advisorPhaseLabel} - Encuentro ${info.order || advisorMeetingOrder}`.trim()
                : 'Lista de asistencia'}
            </p>
          </div>
          {!isAdvisorPhaseMeeting && (
            <EvidenceReviewStatus
              status={info.reviewStatus}
              observations={info.reviewObservations}
            />
          )}
        </div>
        <div className='inputs-container'>

          <label className='input-date-container' htmlFor='single-meeting-date'>
            {isReadOnlyView && <p>{normalizeTextValue(info.meetingDate)}</p>}
            {!isReadOnlyView && (
              <input
                id='single-meeting-date'
                type='date'
                min='2026-01-01'
                max='2026-12-31'
                onChange={e => addInfo(e.target.value, 'meetingDate')}
                onBlur={e => {
                  if (!isValid2026MeetingDate(e.target.value)) {
                    toast('Fecha no válida. Debe corresponder al año 2026', {
                      type: 'warning'
                    });
                  }
                }}
                onInvalid={e => {
                  e.preventDefault();
                  toast('Fecha no válida. Debe corresponder al año 2026', {
                    type: 'warning'
                  });
                }}
                className='input-form'
                value={normalizeTextValue(info.meetingDate)}
              />
            )}
          </label>
          <div className='meeting-time-fields'>
            <label className='input-date-container' htmlFor='single-meeting-start-time'>
              <span>
                Hora de inicio{hasOptionalAdvisorPhaseTimes ? ' (opcional)' : ''}
              </span>
              <input
                id='single-meeting-start-time'
                type='time'
                onChange={e => addInfo(e.target.value, 'startTime')}
                className='input-form'
                value={normalizeTextValue(info.startTime)}
                readOnly={isReadOnlyView}
              />
            </label>
            <label className='input-date-container' htmlFor='single-meeting-end-time'>
              <span>
                Hora de finalizacion{hasOptionalAdvisorPhaseTimes ? ' (opcional)' : ''}
              </span>
              <input
                id='single-meeting-end-time'
                type='time'
                onChange={e => addInfo(e.target.value, 'endTime')}
                className='input-form'
                value={normalizeTextValue(info.endTime)}
                readOnly={isReadOnlyView}
              />
            </label>
          </div>
          <label className='input-topic-container' htmlFor='single-meeting-topic'>
            <span>Tema</span>
            <input
              id='single-meeting-topic'
              type='text'
              placeholder='Tema del encuentro'
              onChange={e => addInfo(e.target.value, 'topic')}
              className='input-form'
              value={info.topic}
              maxLength={topicMaxLength}
              readOnly={isReadOnlyView}
              aria-describedby='single-meeting-topic-limit'
            />
            <small id='single-meeting-topic-limit'>
              {info.topic.length}/{topicMaxLength} caracteres
              {info.topicCode && ` · Se añadirá: - ${info.topicCode}`}
            </small>
          </label>
          <div className='meeting-location-container'>
            <button
              type='button'
              className='meeting-location-toggle'
              aria-expanded={info.hasCustomLocation}
              disabled={isReadOnlyView}
              onClick={() => addInfo(!info.hasCustomLocation, 'hasCustomLocation')}>
              ¿El lugar y la dirección son diferentes al registrado en la institución?{' '}
              <span>{info.hasCustomLocation ? 'Ocultar y usar los datos de la institución' : 'Click aquí para cambiarlos'}</span>
            </button>
            {info.hasCustomLocation && (
              <div className='meeting-location-fields'>
                <label htmlFor='single-meeting-place'>
                  <span>Lugar</span>
                  <input
                    id='single-meeting-place'
                    type='text'
                    placeholder='Lugar del encuentro'
                    className='input-form'
                    value={normalizeTextValue(info.place)}
                    maxLength={255}
                    readOnly={isReadOnlyView}
                    onChange={e => addInfo(e.target.value, 'place')}
                  />
                </label>
                <label htmlFor='single-meeting-address'>
                  <span>Dirección</span>
                  <input
                    id='single-meeting-address'
                    type='text'
                    placeholder='Dirección del encuentro'
                    className='input-form'
                    value={normalizeTextValue(info.address)}
                    maxLength={255}
                    readOnly={isReadOnlyView}
                    onChange={e => addInfo(e.target.value, 'address')}
                  />
                </label>
              </div>
            )}
          </div>
          <label className='input-area-container' htmlFor='single-meeting-goal'>
            <textarea
              id='single-meeting-goal'
              type='topic'
              placeholder={
                isSocialControlMediator ? 'Objetivo (opcional)' : 'Objetivo'
              }
              onChange={e => addInfo(e.target.value, 'goal')}
              className='input-form'
              value={normalizeTextValue(info.goal)}
              maxLength={2000}
              readOnly={isReadOnlyView}
            />
          </label>
          <label className='input-area-container' htmlFor='single-meeting-notes'>
            <textarea
              id='single-meeting-notes'
              type='topic'
              placeholder='Observaciones'
              onChange={e => addInfo(e.target.value, 'meetingNotes')}
              className='area'
              value={normalizeTextValue(info.meetingNotes)}
              max={255}
              readOnly={isReadOnlyView}
            />
          </label>
        </div>
        <div className='photos-register-container'>
          <p className='title'>Evidencias</p>
          <p className='evidence-size-warning'>
            El tamaño total de todos los archivos seleccionados no puede superar 5 MB.
          </p>
          <div className='assistence-photos evidence-groups'>
            {evidenceSections.map((sectionTitle, index) => {
              const imgInfo = attendanceImages[index] || {
                id: index,
                updated: false,
                imageUrl: '',
                fileType: null,
                externalId: null,
                fileName: ''
              };
              const evidenceLabel = getEvidenceDisplayName(imgInfo, index);

              return (
                <div key={sectionTitle} className='evidence-group'>
                  <p className='evidence-group__title'>{sectionTitle}</p>
                  <div className='evidence-item'>
                    {imgInfo.updated ? (
                      imgInfo.fileType === 'pdf' ? (
                        <button
                          type='button'
                          className='photo pdf-preview'
                          onClick={() => handleImageClick(index)}
                        >
                          PDF
                        </button>
                      ) : (
                        <img
                          key={index}
                          className='photo'
                          src={imgInfo.imageUrl}
                          alt={`Evidencia ${index + 1}`}
                          onError={(e) => {
                            if (!imgInfo?.externalId) return;
                            const fallbackUrl = buildProtectedImageUrl(imgInfo.externalId, false);
                            if (e.currentTarget.src !== fallbackUrl) {
                              e.currentTarget.src = fallbackUrl;
                            }
                          }}
                          onClick={() => handleImageClick(index)}
                        />
                      )
                    ) : (
                      <img
                        key={index}
                        className='iconWhite'
                        src={AddIconWhite}
                        alt={`Evidencia ${index + 1}`}
                        onClick={() => handleImageClick(index)}
                      />
                    )}
                    <p
                      className='evidence-file-name'
                      title={evidenceLabel}>
                      {evidenceLabel}
                    </p>
                    {imgInfo.updated && (
                      <button
                        type='button'
                        className='evidence-preview-button'
                        onClick={() => openEvidencePreview(index)}
                      >
                        PREVISUALIZAR
                      </button>
                    )}
                    <input
                      accept='image/*,.pdf,application/pdf,.jpg,.jpeg,image/jpeg,image/jpg'
                      id={`icon-button-file${index}`}
                      key={`button-file${index}`}
                      type='file'
                      ref={ref => setInputRef(index, ref)}
                      onChange={e => handleCaptureImage(e.target, index)}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {hasAttendance && (
          <>
            <p className='total-beneficiaries'>
              Total de beneficiarios inscritos: <span>{beneficiaries?.length}</span>
            </p>
            <div className='beneficiaries-list'>
              {beneficiaries &&
                beneficiaries?.length > 0 &&
                beneficiaries.map(beneficiary => (
                  <UserAttendanceCard key={beneficiary.id} data={beneficiary} meetingId={info.meetingId} readOnly={isReadOnlyView} />
                ))}
            </div>
          </>
        )}
        <div className='save-button-container'>
          {!isReadOnlyView && (
            <button
              className='blue-site-button saveAll'
              onClick={saveMeeting}
              disabled={buttonDisabled || isDownloadingAttendanceTemplate}>
              GUARDAR
            </button>
          )}
          {!isReadOnlyView && hasAttendance && (
            <button
              className='button-white-border-blue downloadReport'
              type='button'
              onClick={downloadAttendanceTemplate}
              disabled={buttonDisabled || isDownloadingAttendanceTemplate}>
              {isDownloadingAttendanceTemplate ? 'DESCARGANDO LISTADO...' : 'Descargar listado'}
            </button>
          )}
          {!isReadOnlyView && hasAttendance && (
            <button className='button-white-border-blue downloadReport' type='button'
                    onClick={() => generateAttendanceQr('attendance_only')} disabled={isGeneratingAttendanceQr}>
              {isGeneratingAttendanceQr ? 'GENERANDO QR...' : 'GENERAR QR DE ASISTENCIA'}
            </button>
          )}
          {!isReadOnlyView && hasAttendance && info.programScope === 'DIPLOM' && (
            <button className='button-white-border-blue downloadReport' type='button'
                    onClick={() => generateAttendanceQr('attendance_satisfaction')}
                    disabled={isGeneratingAttendanceQr}>
              {isGeneratingAttendanceQr
                ? 'GENERANDO QR...'
                : 'ASISTENCIA + FORMULARIO DE SATISFACCIÓN'}
            </button>
          )}
          {!isReadOnlyView && hasAdvisorAttendance && (
            <button className='button-white-border-blue downloadReport' type='button'
                    onClick={() => generateAttendanceQr('attendance_characterization')}
                    disabled={isGeneratingAttendanceQr}>
              {isGeneratingAttendanceQr ? 'GENERANDO QR...' : 'GENERAR QR CARACTERIZACIÓN + ASISTENCIA'}
            </button>
          )}
          <button
            className='button-white-border-blue downloadReport'
            onClick={handleDownloadMeetingReport}
            disabled={buttonDisabled}>
            DESCARGAR
          </button>
          <button
            className='button-white-border-blue downloadReport'
            onClick={handleDownloadUnifiedMeetingEvidences}
            disabled={buttonDisabled}>
            DESCARGAR UNIDO
          </button>
        </div>
      </div>
    </section>
  );
}

function EvidenceReviewStatus({ status, observations }) {
  const normalizedStatus = String(status || 'pending').trim().toLowerCase();
  const statusInfo = EVIDENCE_REVIEW_STATUS[normalizedStatus] ||
    EVIDENCE_REVIEW_STATUS.pending;
  const normalizedObservations = String(observations || '').trim();
  const badge = (
    <span className={`evidence-review-status__badge is-${normalizedStatus}`}>
      <span className='material-symbols-rounded'>{statusInfo.icon}</span>
      {statusInfo.label}
      {normalizedObservations && (
        <span className='material-symbols-rounded evidence-review-status__expand'>expand_more</span>
      )}
    </span>
  );

  if (!normalizedObservations) {
    return <div className='evidence-review-status'>{badge}</div>;
  }

  return (
    <details className='evidence-review-status'>
      <summary aria-label={`${statusInfo.label}. Ver observaciones`}>
        {badge}
      </summary>
      <div className='evidence-review-status__observations'>
        <strong>Observaciones del revisor</strong>
        <p>{normalizedObservations}</p>
      </div>
    </details>
  );
}

export default SingleMeeting;
